import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type NavItemNoIcon, navItemType } from '@cx-web/domain-types';
import classNames from 'classnames';

import Icon from '../../../../primitives/icon/index';
import { mergePersistentParams } from '../../utils/merge-persistent-params';

import styles from './MoreDropdown.module.css';

type MoreDropdownProps = {
  items: NavItemNoIcon[];
  serverUrl: string;
  // 'tertiary' = L3 white-bar multi-column grid; 'primary' (default) = the shared L1 popover.
  variant?: 'primary' | 'tertiary';
};

const isInternalLink = (link: string) =>
  link.includes('oddschecker.com') ||
  link.includes('occloud.io') ||
  link.includes('localhost') ||
  link.startsWith('/') ||
  link.startsWith('#');

const MoreDropdown: React.FC<MoreDropdownProps> = ({ items, serverUrl, variant = 'primary' }) => {
  const { t } = useTranslation(['label']);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Unique per instance so aria-controls/id stays valid if more than one dropdown renders on a page.
  const panelId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const dropdownItemLinks = items.filter(item => item.type === navItemType.LINK);
  if (!dropdownItemLinks.length) return null;

  const label = t('RESPONSIVE_MORE');
  const selectedPath = new URL(serverUrl).pathname;

  return (
    <div
      className={classNames(styles.container, variant === 'tertiary' && styles.tertiary)}
      data-open={isOpen}
      data-testid="more-dropdown"
      data-variant={variant}
      ref={containerRef}
    >
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={styles.toggle}
        data-testid="more-dropdown-trigger"
        onClick={() => setIsOpen(prev => !prev)}
        type="button"
      >
        {label}
      </button>

      <div className={styles.panelWrapper}>
        <div aria-label={label} className={styles.panel} id={panelId} role="group">
          <ul className={styles.list}>
            {dropdownItemLinks.map(({ labelPill, link = '', name }, index) => {
              const isSelected = link === selectedPath;

              return (
                <li className={styles.item} key={link || name || index}>
                  <a
                    aria-current={isSelected ? 'page' : undefined}
                    className={styles.link}
                    data-testid="more-dropdown-link"
                    href={mergePersistentParams(link, serverUrl)}
                    rel={isInternalLink(link) ? undefined : 'nofollow'}
                  >
                    {name}
                    {labelPill && (
                      <span aria-hidden="true" className={styles.labelPill}>
                        {labelPill}
                      </span>
                    )}
                    {isSelected && <Icon className={styles.selectedIcon} height={16} icon="CircleTick" width={16} />}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MoreDropdown;
